import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { Plus, X } from 'lucide-react';

export default function BudgetCalc() {
  const [income, setIncome] = useState(6000);
  const [expenses, setExpenses] = useState([
    { name: 'Rent/Mortgage', amount: 1500 },
    { name: 'Utilities', amount: 200 },
    { name: 'Food', amount: 600 },
    { name: 'Transportation', amount: 400 },
    { name: 'Insurance', amount: 300 },
    { name: 'Taxes', amount: 800 },
    { name: 'Other', amount: 200 }
  ]);
  const SUGGESTIONS = [
    { cat: 'Housing',        items: ['Rent / Mortgage', 'Property Tax', 'HOA / Condo Fees', 'Home Insurance', 'Home Repairs', 'Household Supplies'] },
    { cat: 'Utilities',      items: ['Electricity', 'Gas / Heating', 'Water & Sewer', 'Trash / Recycling', 'Internet', 'Cable / Streaming', 'Phone Bill'] },
    { cat: 'Transportation', items: ['Car Payment', 'Car Insurance', 'Gasoline', 'Car Maintenance', 'Public Transit', 'Parking', 'Tolls', 'Ride-share'] },
    { cat: 'Food',           items: ['Groceries', 'Dining Out', 'Coffee / Snacks', 'Work Lunches', 'Meal Delivery'] },
    { cat: 'Insurance',      items: ['Health Insurance', 'Life Insurance', 'Disability', 'Renters Insurance', 'Umbrella Policy'] },
    { cat: 'Debt',           items: ['Credit Card Payment', 'Student Loans', 'Personal Loan', 'Medical Debt'] },
    { cat: 'Entertainment',  items: ['Streaming Services', 'Movies / Concerts', 'Hobbies', 'Books / Games', 'Vacations', 'Gym Membership'] },
    { cat: 'Savings',        items: ['Emergency Fund', 'Retirement (401k / IRA)', '529 College Plan', 'Investments', 'Down Payment Fund'] },
    { cat: 'Personal',       items: ['Clothing', 'Haircut / Salon', 'Toiletries', 'Gifts', 'Subscriptions', 'Charity'] },
    { cat: 'Family',         items: ['Childcare', 'Kids Activities', 'School Tuition', 'Pet Food', 'Vet Bills'] },
  ];
  const normalizeName = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const addFromSuggestion = (name) => {
    const key = normalizeName(name);
    if (expenses.some(e => normalizeName(e.name) === key)) return;
    setExpenses(prev => [...prev, { name, amount: 0 }]);
  };
  const total = expenses.reduce((s, e) => s + (+e.amount || 0), 0);
  const surplus = income - total;
  const fmt = n => '$' + n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return (
    <CalcShell look="cnet" title="Budget Calculator" subtitle="Track monthly income vs expenses and see how much you can save.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Monthly Income ($)</label><input type="number" value={income} onChange={e => setIncome(+e.target.value || 0)} /></div>
          <h3 className="font-bold mt-4 mb-2">Expenses</h3>
          {expenses.map((ex, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center">
              <input value={ex.name} onChange={e => { const n = [...expenses]; n[i].name = e.target.value; setExpenses(n); }} className="col-span-7 px-3 py-2 border border-slate-300 rounded-lg bg-white text-sm" />
              <input type="number" value={ex.amount} onChange={e => { const n = [...expenses]; n[i].amount = +e.target.value || 0; setExpenses(n); }} className="col-span-4 px-3 py-2 border border-slate-300 rounded-lg bg-white text-sm" />
              <button onClick={() => setExpenses(expenses.filter((_, x) => x !== i))} className="text-slate-400 hover:text-red-600"><X className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={() => setExpenses([...expenses, { name: '', amount: 0 }])} className="flex items-center gap-1 text-sm text-blue-600 font-medium mt-2" data-testid="budget-add-blank"><Plus className="w-4 h-4" /> Add blank expense</button>

          {/* Suggested expenses catalog */}
          <div className="mt-6 border-t border-slate-200 pt-4">
            <h4 className="font-semibold text-slate-900 mb-2">Suggested Expenses</h4>
            <p className="text-xs text-slate-500 mb-3">Click any line to add it to your budget. Grayed items are already added.</p>
            <div className="space-y-3">
              {SUGGESTIONS.map(group => (
                <div key={group.cat}>
                  <div className="text-xs font-bold text-blue-700 uppercase tracking-wide mb-1.5">{group.cat}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {group.items.map(item => {
                      const already = expenses.some(e => normalizeName(e.name) === normalizeName(item));
                      return (
                        <button
                          key={item}
                          onClick={() => addFromSuggestion(item)}
                          disabled={already}
                          data-testid={`budget-sug-${item.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                          className={`px-2.5 py-1 rounded-full text-xs border transition ${already ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed' : 'bg-white text-slate-700 border-slate-300 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-700'}`}
                        >
                          {already ? '✓ ' : '+ '}{item}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div>
          <div className="result-panel mb-4">
            <div className="result-title">Monthly Surplus</div>
            <div className="result-value" style={{ color: surplus >= 0 ? '#059669' : '#dc2626' }}>{fmt(surplus)}</div>
          </div>
          <table className="data-table"><tbody>
            <tr><td>Total Income</td><td className="text-right">{fmt(income)}</td></tr>
            <tr><td>Total Expenses</td><td className="text-right">{fmt(total)}</td></tr>
            <tr><td>Expense Ratio</td><td className="text-right font-semibold">{income > 0 ? (total/income*100).toFixed(1) : 0}%</td></tr>
          </tbody></table>

          {/* Pie chart with legend */}
          <div className="mt-4 grid grid-cols-2 gap-4 items-center">
            <svg viewBox="0 0 200 200" width="180" height="180">
              {(() => {
                const colors = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#14b8a6','#f97316'];
                let acc = 0;
                return expenses.map((ex, i) => {
                  const pct = total > 0 ? ex.amount/total : 0;
                  const a0 = acc * Math.PI * 2 - Math.PI/2;
                  acc += pct;
                  const a1 = acc * Math.PI * 2 - Math.PI/2;
                  const large = pct > 0.5 ? 1 : 0;
                  const x0 = 100 + 80*Math.cos(a0), y0 = 100 + 80*Math.sin(a0);
                  const x1 = 100 + 80*Math.cos(a1), y1 = 100 + 80*Math.sin(a1);
                  return <path key={i} d={`M100 100 L${x0} ${y0} A80 80 0 ${large} 1 ${x1} ${y1} Z`} fill={colors[i % colors.length]} opacity="0.85" />;
                });
              })()}
            </svg>
            <div className="space-y-1 text-xs">
              {(() => {
                const colors = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#14b8a6','#f97316'];
                return expenses.map((ex, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded" style={{background: colors[i % colors.length]}}></span>
                    <span className="text-slate-700 flex-1 truncate">{ex.name || '—'}</span>
                    <span className="font-semibold text-slate-900">{total > 0 ? ((ex.amount/total)*100).toFixed(1) : 0}%</span>
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      </div>
    </CalcShell>
  );
}
